import {
  assertIsTruthy,
  coalesce,
  filterFalsy,
  objectKeys,
} from "@kazoohr/helpers";

import { locales } from "./index";

export type SupportedLanguage = keyof typeof locales;

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] =
  objectKeys(locales);

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: "English",
};

export function isSupportedLanguage(
  value: unknown
): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.some((language) => language === value);
}

export function assertIsSupportedLanguage(
  value: unknown
): asserts value is SupportedLanguage {
  assertIsTruthy(
    isSupportedLanguage(value),
    `Unsupported locale: ${String(value)}`
  );
}

function baseLanguage(language: string): string {
  return language.toLowerCase().split("-")[0];
}

export function matchLanguage<L extends string>(
  candidates: readonly (string | null | undefined)[],
  supported: readonly L[],
  fallback: L
): L {
  for (const candidate of filterFalsy(candidates)) {
    const lowerCandidate = candidate.toLowerCase();
    const exact = supported.find(
      (language) => language.toLowerCase() === lowerCandidate
    );
    const sameBase = supported.find(
      (language) => baseLanguage(language) === baseLanguage(candidate)
    );
    const match = coalesce(exact, sameBase);
    if (match) {
      return match;
    }
  }
  return fallback;
}
